<template>
  <!-- Data table -->
  <div
    class="q-table"
    id="tableTable"
  >
    <vuetable
      ref="vuetable"
      class="table-block q-mb-md"
      :class="{ 'is-loading': isLoading }"
      :table-height="tableHeight"
      :api-url="endpoint"
      pagination-path
      :api-mode="true"
      :css="css.table"
      :noDataTemplate="noDataString(responseFormat)"
      :http-fetch="fetchData"
      :fields="columns"
      :row-class="selectable ? applyRowClass : null"
      @vuetable:loading="onLoading"
      @vuetable:load-success="onSuccess"
      @vuetable:load-error="onError"
      @vuetable:pagination-data="onPaginationData"
      @vuetable:row-clicked="onRowClicked"
    >

      <template v-for="(name,key) in highlightFields" slot-scope="props" :slot="name">
        <div :key="key">
          <div v-html="highlight(props.rowData[name], filters[name])"></div>
        </div>
      </template>

      <div
        slot="location_from"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.location_from, filters.location_from)"></div>
      </div>
      <div
        slot="location_to"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.location_to, filters.location_to)"></div>
      </div>
      <div
        slot="catch_all"
        slot-scope="props"
      >
        <q-icon :name="props.rowData.catch_all ? 'done' : ''" />
      </div>
      <!-- general title -->
      <div
        slot="general-title"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.title, filters.title)"></div>
      </div>
      <!-- general term -->
      <div
        slot="general-term"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.term, filters.term)"></div>
      </div>
      <!-- CUSTOM SLOTS -->
      <div
        slot="domain"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.domain, filters.domain)"></div>
      </div>
      <!-- Datetime online -->
      <div slot="datetime-online" slot-scope="props">
        <div class="text-ellipsis" v-html="(props.rowData.datetime && props.rowData.status !== 'draft') ?
          tableFormatTime(props.rowData.datetime, ) :
          'Concept'
          ">
        </div>
      </div>
      <!-- Promotions:name -->
      <div
        slot="locked"
        slot-scope="props"
      >
        <div v-if="props.rowData.locked">
          <q-icon
            size="large"
            name="lock"
          />
          <q-tooltip :delay="200">
            <div class="lock-info__tooltip"></div>
            {{ getString('content.lock.overview.info').replace('%name%', fullname(props.rowData.locked.user.name)).replace('%time%', tableFormatTime(props.rowData.locked.datetime)) }}
          </q-tooltip>
        </div>
        <q-icon
          v-else
          size="large"
          name="lock_open"
        />
      </div>
      <div
        slot="promotions-name"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.name, filters.name)"></div>
      </div>

      <!-- Navigation:title -->
      <div
        slot="nav-title"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.title, filters.title)"></div>
      </div>

      <!-- Tags:Label -->
      <div
        slot="tags-label"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.label, filters.label)"></div>
      </div>

      <!-- Tags:Url -->
      <div
        slot="general-url"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.url, filters.url)"></div>
      </div>

      <!-- Users:Name -->
      <div
        slot="users-name"
        class="user-slot"
        slot-scope="props"
      >
        <!-- User avatar -->
        <img
          v-if="!fallbacks.includes(props.rowData.id)"
          :src="userAvatar(props.rowData.avatar)"
          @error="fallbackImage(props.rowData.id)"
          class="table-gravatar"
        >
        <img
          v-else
          :src="`${base_url}default-avatar.jpeg`"
          class="table-gravatar"
        >
        <!-- User full name -->
        <div class="user-name">
          <div v-html="highlight(fullname(props.rowData.name), filters.name)"></div>
        </div>
      </div>

      <!-- Users:Email -->
      <div
        slot="users-email"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.email, filters.email)"></div>
      </div>

      <!-- Users:Active -->
      <div
        slot="users-active"
        slot-scope="props"
      >
        <q-toggle
          v-model="props.rowData.active"
          @input="toggleActive(props.rowData)"
          color="secondary"
        />
      </div>

      <!-- Usergroups:Name -->
      <div
        slot="general-name"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.name, filters.name)"></div>
      </div>

      <!-- Last updated -->
      <div
        slot="usergroups-datetime"
        slot-scope="props"
      >
        <div
          class="text-ellipsis"
          v-html="(props.rowData.datetime.update) ?
              tableFormatTime(props.rowData.datetime.update) :
              tableFormatTime(props.rowData.datetime.added)"
        >
        </div>
      </div>

      <!-- Datetime start -->
      <div
        slot="datetime_start"
        slot-scope="props"
      >
        <div
          class="text-ellipsis"
          v-html="(props.rowData.datetime_start)"
        >
        </div>
      </div>

      <!-- Features:Label -->
      <div
        slot="features-label"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.label, filters.label)"></div>
      </div>

      <!-- Features:Category -->
      <div
        slot="features-category"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.category && props.rowData.category.name, filters.category)"></div>
      </div>

      <!-- Features:Code -->
      <div
        slot="features-code"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.code, filters.code)"></div>
      </div>

      <!-- Features:Visible -->
      <div
        slot="features-visible"
        slot-scope="props"
      >
        <q-toggle
          v-model="props.rowData.visible"
          @input="toggle(props.rowData, 'visible')"
          color="secondary"
        />
      </div>

      <!-- Features:Filterable -->
      <div
        slot="features-filterable"
        slot-scope="props"
      >
        <q-toggle
          v-model="props.rowData.filterable"
          @input="toggle(props.rowData, 'filterable')"
          color="secondary"
        />
      </div>

      <!-- Categories:name -->
      <div
        slot="category-name"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.name, filters.name)"></div>
      </div>

      <!-- Categories:description -->
      <div
        slot="category-description"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.description, filters.description)"></div>
      </div>

      <!-- Structure:Code -->
      <div
        slot="structure-code"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.code, filters.code)"></div>
      </div>

      <!-- Structure:Description -->
      <div
        slot="structure-description"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.description, filters.description)"></div>
      </div>

      <!-- Structure:Active -->
      <div
        slot="structure-active"
        slot-scope="props"
      >
        <q-toggle
          v-model="props.rowData.active"
          readonly
          color="secondary"
        />
      </div>

      <!-- Structure:Active -->
      <div
        slot="target-audience"
        slot-scope="props"
      >
        {{ mapTargets(props.rowData.targets) }}
      </div>

      <!-- Errorcode:Standard unit -->
      <div
        slot="unit-standard"
        slot-scope="props"
      >
        <q-toggle
          v-model="props.rowData.standard"
          :readonly="true"
          color="secondary"
        />
      </div>
      <!-- Errorcode:Inverter unit -->
      <div
        slot="unit-inverter"
        slot-scope="props"
      >
        <q-toggle
          v-model="props.rowData.inverter"
          :readonly="true"
          color="secondary"
        />
      </div>

      <!-- Errorcode: Code -->
      <div
        slot="error-code"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.code, filters.code)"></div>
      </div>

      <!-- Errorcode: unit -->
      <div
        slot="error-source"
        slot-scope="props"
      >
        <div v-html="getString(`errorcode.source.${props.rowData.source}`)"></div>
      </div>

      <!-- Popup -->
      <div
        slot="popup-popup"
        slot-scope="props"
      >
        <q-toggle
          v-model="props.rowData.popup"
          @input="togglePopup('popup', props.rowData)"
          color="secondary"
        />
      </div>

      <!-- Popup : once only -->
      <div
        slot="popup-once"
        slot-scope="props"
      >
        <q-toggle
          @input="togglePopup('show_once', props.rowData)"
          v-model="props.rowData.show_once"
          color="secondary"
        />
      </div>

      <!-- Task : start -->
      <div
        slot="task-start"
        slot-scope="props"
      >
        <q-btn
          :disabled="!props.rowData.runnable"
          round
          size="sm"
          :color="props.rowData.runnable ? 'secondary' : 'light'"
          icon="play_arrow"
          @click="dispatchTask(props.rowData)"
        />
      </div>

      <!-- Task : parameters -->
      <div
        slot="task-parameters"
        slot-scope="props"
      >
        <p
          :key="k"
          v-for="[k, v] of Object.entries(JSON.parse(props.rowData.parameters))"
        >
          {{ k }}:
          <template v-if="typeof v === 'boolean'">
            {{ v === true ? 'yes' : 'no' }}
          </template>
          <template v-else>
            {{ v }}
          </template>
        </p>
      </div>

      <!-- general title -->
      <div
        slot="popup-content"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.content, filters.content)"></div>
      </div>

      <div
        slot="coupon-code"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.code, filters.code)"></div>
      </div>

      <!-- Last updated -->
      <div
        slot="coupon-start"
        slot-scope="props"
      >
        <div
          class="text-ellipsis"
          v-html="tableFormatTime(props.rowData.valid.from, 'MM-DD-YYYY')"
        >
        </div>
      </div>

      <!-- Last updated -->
      <div
        slot="coupon-end"
        slot-scope="props"
      >
        <div
          class="text-ellipsis"
          v-html="tableFormatTime(props.rowData.valid.to, 'MM-DD-YYYY')"
        >
        </div>
      </div>

      <div
        slot="spareparts-name"
        slot-scope="props"
      >
        <div v-html="highlight(props.rowData.names && props.rowData.names.length ? props.rowData.names[0] : '', filters.name)"></div>
      </div>
      <div
        slot="spareparts-reference"
        slot-scope="props"
      >
        <div v-html="highlight(formatServiceReferences(props.rowData), filters.name)"></div>
      </div>
      <div
        slot="spareparts-reference-score"
        slot-scope="props"
      >
      <div class="pdf-status" style="display: flex;">
          <dot-status :class="getReferenceStatusClass(props.rowData.reference_score)">
            {{  getString(null, 'A part is matched when at least one reference has a quantity of gte 1.', 'Er is sprake van een match als ten minste één referentie een hoeveelheid van 1 of meer heeft') }}
          </dot-status>
          <span class="center-vertical">
            {{ props.rowData.reference_score }}%
          </span>
      </div>
      </div>
      <div
        slot="spareparts-manual"
        slot-scope="props"
        >
        <div class="pdf-status">
          <span class="pdf-status__indicate" :class="mapSparePartsStatus(props.rowData)"></span>
          <span>
            {{ props.rowData.current_match_percentage ? `${props.rowData.current_match_percentage }%` : '0%'}}
          </span>
        </div>
      </div>
      <div
        slot="spareparts-process"
        slot-scope="props"
        >
        <div class="pdf-status">
          <span class="pdf-status__indicate" :class="mapSparePartsProcess(props.rowData)"></span>
          <span>
            {{ props.rowData.process_match_percentage ? `${props.rowData.process_match_percentage }%` : '0%'}}
          </span>
        </div>
      </div>
      <!-- Spareparts: Checked -->
      <div
        slot="spareparts-checked"
        slot-scope="props"
      >
        <q-toggle
          v-model="props.rowData.check_status"
          :disable="!props.rowData.hasOwnProperty('check_status')"
          @input="toggleChecked(props)"
          true-value="CHECKED"
          false-value="UNCHECKED"
          color="secondary"
        />
      </div>
      <!-- Spareparts: Activated -->
      <div
        slot="spareparts-activated"
        slot-scope="props"
      >
        <q-toggle
          v-model="props.rowData.activated"
          :disable="!props.rowData.hasOwnProperty('activated')"
          @input="toggleChecked(props)"
          color="secondary"
        />
      </div>
            <!-- Emails: Activated -->
      <div
        slot="sending-enabled"
        slot-scope="props"
      >
        <q-toggle
          v-model="props.rowData.sending_enabled"
          :disable="!props.rowData.hasOwnProperty('sending_enabled')"
          @input="toggleChecked(props)"
          false-value=0
          true-value=1
          color="secondary"
        />
      </div>

      <!-- Email categories -->
      <div slot="email-category" slot-scope="props">
          <div>{{ findJoinById(props.rowData.category_id).name }}</div>
      </div>
      <!-- Level slot -->
      <div slot="level" slot-scope="props" class="message-slot">
        <div class="log-message" :class="messageClass(props.rowData.level)"></div>
        <div>
          {{ props.rowData.level }}
        </div>
      </div>

      <div slot="locations-name" slot-scope="props">
          <div v-html="highlight(props.rowData.name, filters.name)"></div>
      </div>

      <div slot="locations-city" slot-scope="props">
          <div
              v-html="highlight(props.rowData.address.city, filters.city)"
          ></div>
      </div>

      <div slot="course-title" slot-scope="props">
          <div
              v-html="highlight(props.rowData.title, filters.title)"
          ></div>
      </div>

      <div slot="course-location" slot-scope="props">
          <div
              v-if="props.rowData.location  && props.rowData.location.name"
              v-html="
                  highlight(props.rowData.location.name, filters.location)
              "
          ></div>
          <div v-else>-</div>
      </div>

      <div slot="planning-location" slot-scope="props">
          <div
              v-if="props.rowData.location && props.rowData.location.name"
              v-html="
                  highlight(props.rowData.location.name, filters.location)
              "
          ></div>
          <div v-else>-</div>
      </div>

      <div slot="planning-course" slot-scope="props">
          <div
              v-html="
                  highlight(props.rowData.course.title, filters.course)
              "
          ></div>
      </div>

      <div slot="planning-active" slot-scope="props">
          <q-icon :name="props.rowData.active ? 'done' : 'clear'" />
      </div>

      <div slot="planning-date" slot-scope="props">
        <div v-html="tableFormatTime(props.rowData.datetime_start, 'DD MMMM YYYY, HH:mm')"></div>
      </div>

      <div slot="planning-full" slot-scope="props">
          <q-icon :name="props.rowData.full ? 'done' : 'clear'" />
      </div>

      <div slot="etim-template" slot-scope="props">
        <div v-html="formatEtimFeatures(props.rowData)"></div>
      </div>

      <div slot="search_params" slot-scope="props">
        <div v-html="formatSearchParams(props.rowData)"></div>
      </div>
    </vuetable>

    <!-- Pagination -->
    <div
      class="row pagination-bottom"
      id="tablePagination"
      v-if="pagination"
    >
      <!-- Per page select -->
      <div class="col">
        <div class="row">
          <span class="q-mr-sm grey">
            {{ $store.state.stringsStore.strings.list.itemsPerPage }}
          </span>
          <q-select
            class="btn-compact select-small"
            hide-underline
            color="grey"
            v-model="perPage"
            :options="perPageOptions"
          />
        </div>
      </div>
      <div class="col right-margin">
        <vuetable-pagination
          :on-each-side="1"
          :css="css.pagination"
          @vuetable-pagination:change-page="onChangePage"
          ref="pagination"
        >
        </vuetable-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import qs from 'qs';

// Helpers
import getString from '@/helpers/GetStrings';
import EventBus from '@/event-bus';
import tableMixin from '@/mixins/tables/tableMixin';

// Components
import Vuetable from 'vuetable-2';
import VuetablePagination from 'vuetable-2/src/components/VuetablePagination.vue';
// Components
import VariableTag from '@/components/VariableTag.vue';
import DotStatus from '@/components/status/DotStatus.vue';

export default {
  mixins: [tableMixin],
  data() {
    return {
      env_url: process.env.VUE_APP_API_URL,
      base_url: process.env.BASE_URL,
      fallbacks: [],
      perPageOptions: [
        {
          label: '10',
          value: 10,
        },
        {
          label: '25',
          value: 25,
        },
        {
          label: '50',
          value: 50,
        },
        {
          label: '100',
          value: 100,
        },
      ],
      page: 1,
      perPage: 10,
      lastPage: null,
      preferences: {
        page: 1,
        perPage: 10,
        sortField: null,
        sortOrder: null,
      },
      rowSelection: [],
      rowData: [],
      searchTerm: '',
      filters: {},
      isLoading: false,
      css: {
        table: {
          tableClass: 'vuetable ui selectable celled stackable attached table vuetable-fixed-layout',
          loadingClass: '',
          ascendingIcon: 'mdi mdi-chevron-up icon',
          descendingIcon: 'mdi mdi-chevron-down icon',
          handleIcon: '',
        },
        pagination: {
          wrapperClass: 'pagination',
          activeClass: 'active',
          disabledClass: 'disabled',
          pageClass: '',
          linkClass: '',
          icons: {
            first: 'mdi mdi-chevron-double-left icon',
            prev: 'mdi mdi-chevron-left icon',
            next: 'mdi mdi-chevron-right icon',
            last: 'mdi mdi-chevron-double-right icon',
          },
        },
      },
      dateFormat: {
        'yyyy-MM-dd': 'YYYY-MM-DD',
        'dd-MM-yyyy': 'DD-MM-YYYY',
        'd-M-yyyy': 'D-M-YYYY',
        'dd-MM-yy': 'DD-MM-YY',
        'd-M-yy': 'D-M-YY',
      },
    };
  },

  props: {
    tableHeight: {
      type: String,
      default: 'calc(100vh - 280px)',
    },
    customEvents: {
      type: Boolean,
      default: false,
    },
    endpoint: {
      type: String,
      required: true,
    },
    responseFormat: {
      type: String,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    permission: {
      type: String,
      required: true,
    },
    module: {
      type: String,
      required: true,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Boolean,
      default: true,
    },
    highlightFields: {
      type: Array,
      default: () => [],
    },
    rowClickDisabled: {
      type: Boolean,
      default: false,
    },
    // Requires 'field' to join on and entries (array)
    join: {
      type: Array,
      default: () => [],
    },
  },

  components: {
    Vuetable,
    VuetablePagination,
    // eslint-disable-next-line vue/no-unused-components
    VariableTag,
    DotStatus,
  },

  methods: {
    getString,

    formatServiceReferences(rowData) {
      return rowData.references
        && rowData.references.some_references
        && rowData.references.some_references.length
        ? rowData.references.some_references.join(', ') : '';
    },

    formatSearchParams(rowData) {
      return JSON.parse(rowData.params);
    },

    formatEtimFeatures(rowData) {
      let newString = rowData.template;
      rowData.features.forEach((feature) => {
        newString = newString.replace(`%${feature.code}%`, this.createVariableTag(feature));
      });

      return newString;
    },

    createVariableTag(item) {
      const VariableTagComponent = Vue.extend(VariableTag);
      const tag = new VariableTagComponent({ propsData: { name: item.label, className: item.code, onlyReadable: true, disable: true } });
      tag.$mount();
      return tag.$el.outerHTML;
    },

    mapSparePartsStatus(row) {
      let className;
      if (row.current_match_percentage === 0 && !row.processed_at) {
        className = 'pdf-status__indicate--red';
      } else if (row.current_match_percentage === 100) {
        className = 'pdf-status__indicate--green';
      } else {
        className = 'pdf-status__indicate--blue';
      }
      return className;
    },

    mapSparePartsProcess(row) {
      let className;
      if (['PROCESS_FAILED', 'UNPROCESSED'].includes(row.process_status)) {
        className = 'pdf-status__indicate--red';
      } else if (row.process_match_percentage === 100) {
        className = 'pdf-status__indicate--green';
      } else {
        className = 'pdf-status__indicate--blue';
      }
      return className;
    },

    mapTargets(array) {
      const targets = array ? array.map(target => target.label) : [];

      return targets.join(', ');
    },

    findJoinById(id) {
      return this.join.find(x => x.id === id) || {};
    },

    userAvatar(path) {
      return path.includes('www.gravatar.com') ? path : `${process.env.VUE_APP_API_URL}${path}`;
    },

    fallbackImage(id) {
      this.fallbacks.push(id);
    },

    toggle(rowData, property) {
      const isActive = rowData[property] ? 1 : 0;
      this.axios.put(`/${this.endpoint}/${rowData.id}`, qs.stringify({ [property]: isActive }))
        .then(() => {
        })
        .catch((err) => {
          console.warn(err);
        });
    },

    toggleActive(rowData) {
      const isActive = rowData.active ? 1 : 0;
      this.axios.put(`/${this.endpoint}/${rowData.id}`, qs.stringify({ active: isActive }))
        .then(() => {
        })
        .catch((err) => {
          console.warn(err);
        });
    },
    /**
     * Toggles the checked status of a pdf in the spare parts finder module
     */
    toggleChecked(props) {
      const rowData = props.rowData;
      if (props.rowField === 'sending_enabled') {
        // eslint-disable-next-line radix
        this.axios.put(`/emails/toggle-sending/${rowData.id}`, qs.stringify({ sending_enabled: +rowData.sending_enabled }))
          .then(() => this.notify())
          .catch((err) => {
            console.warn(err);
            // Revert to previous value in the overview
            rowData.sending_enabled = !rowData.sending_enabled;
            this.notify(null, 'warning');
          });
      } else {
        const checked = rowData.check_status === 'CHECKED' ? 'CHECKED' : 'UNCHECKED';
        this.axios.put(`/spareparts/pdf/${rowData.id}`, qs.stringify({ check_status: checked, activated: Number(rowData.activated) }))
          .then(() => this.notify(this.$store.state.stringsStore.language === 'nl' ? 'Status bijgewerkt' : 'Status updated'))
          .catch((err) => {
            console.warn(err);
            // Revert to previous value in the overview
            rowData.check_status = rowData.check_status === 'CHECKED' ? 'UNCHECKED' : 'CHECKED';
            this.notify(this.$store.state.stringsStore.language === 'nl' ? 'Status niet bijgewerkt' : 'Unable to update the status', 'warning');
          });
      }
    },

    togglePopup(property, rowData) {
      // eslint-disable-next-line no-nested-ternary
      let value = property === 'popup' ? rowData.popup : property === 'show_once' ? rowData.show_once : null;
      value = value === true ? 1 : 0;
      this.axios.put(`/popup/${rowData.id}`, qs.stringify({ [property]: value }))
        .then(() => {
        })
        .catch((err) => {
          console.warn(err);
        });
    },

    fullname(name) {
      return [name.firstname, name.surname].filter(val => val !== null).join(' ');
    },

    highlight(field, filter) {
      // Make sure null field does not throw error.
      field = field.replace(/(<([^>]+)>)/gi, '') || '';
      // field = field.replace(/<\/?[^>]+>/ig, '') || '';

      const input = this.searchTerm === '' ? filter : this.searchTerm;

      // Only begin higlighting when user has entered 2 or more characters.
      if (input && input.length < 2) {
        return field;
      }

      // Build regex to match query string.
      const regex = new RegExp(input, 'gi');

      // Match and replace query string with highlighted text.
      return field
        .replace(regex, matchedTxt => (`<span class='highlight'>${matchedTxt}</span>`));
    },

    onPaginationData(data) {
      if (!data.meta.pagination || this.$refs.pagination === undefined) {
        return;
      }
      // Save pagination state from response object.
      this.perPage = data.meta.pagination.perPage;
      this.lastPage = data.meta.pagination.total;
      this.preferences.perPage = this.perPage;

      // Adjust pagination data to fit vuetable.
      const paginationData = {
        current_page: this.page,
        last_page: this.lastPage,
        perPage: this.perPage,
      };
      // Set adjusted pagination data in pagination refs.
      this.$refs.pagination.setPaginationData(paginationData);
    },

    onChangePage(page) {
      // Deal with pagination using 'next' and 'prev' strings on page change
      if (page === 'prev') {
        page = this.page > 1 ? this.page - 1 : 0;
      } else if (page === 'next') {
        page = this.page + 1;
      }
      // Prevent request for page number higher than last page or lower than 1.
      if (page > this.lastPage || page < 1) {
        return;
      }
      this.page = page;
      this.$nextTick(() => {
        this.$refs.vuetable && this.$refs.vuetable.refresh();
      });
    },

    // Prevents text selection when using shift to select rows.
    handleShiftKey() {
      document.onselectstart = () => false;
    },

    // Prevents the context menu opening on right-click and ctrl + click.
    handleContextMenu() {
      document.oncontextmenu = () => false;
    },

    // Applies a css class to selected rows.
    applyRowClass(dataItem) {
      return this.rowSelection.indexOf(dataItem.id) > -1 ? 'ctrl-row-select' : '';
    },

    removeRows(key) {
      if (this.rowSelection.length) {
        // Build querystring for the removal
        let selection = [];
        this.rowSelection.forEach((id) => {
          selection.push(`${key}[]=${id}`);
        });
        selection = selection.join('&');

        // Dialog
        this.$q.dialog({
          title: ' ',
          message: (this.rowSelection.length > 1)
            ? this.$store.state.stringsStore.strings.module.library.delete.title.multiple
            : this.$store.state.stringsStore.strings.module.library.delete.title.single,
          ok: this.$store.state.stringsStore.strings.dialog.confirm.ok,
          cancel: this.$store.state.stringsStore.strings.dialog.confirm.cancel,
        }).then(() => {
          // delete call
          this.axios.delete(`${this.endpoint}`, { data: selection })
            .then(() => {
              this.rowSelection = [];
              // Refresh data to see the result
              this.$refs.vuetable && this.$refs.vuetable.refresh();

              // Fetch new me and navigation since the users own rights may have been affected.
              this.$store.dispatch('getMe');
              this.$store.dispatch('getNavigation');
              this.notify((this.rowSelection.length > 1)
                ? this.$store.state.stringsStore.strings.module.library.delete.deleted.multiple
                : this.$store.state.stringsStore.strings.module.library.delete.deleted.single);
            })
            .catch((error) => {
              // Error
              console.warn('error', error);
            });
        }).catch(() => {
          console.info('Nothing removed');
        });
      }
    },

    shiftSelect(row) {
      // rowIndexes is used to store the positions (row numbers) of the rows.
      // This is sorted so that intermediate rows can be added to rowSelection regardless of id.
      const rowIndexes = [];

      // The row numbers of the first & last rows in the selection.
      const firstRow = this.rowData.indexOf(this.rowSelection[0]);
      const lastRow = this.rowData.indexOf(this.rowSelection[this.rowSelection.length - 1]);

      // Push the position of the selected row into rowIndexes.
      rowIndexes.push(this.rowData.indexOf(row.id));

      // Check if clicked row is above the first rowSelection item.
      if (rowIndexes[0] > firstRow) {
        // If true, push the first rowSelection item.
        rowIndexes.push(firstRow);

        // If false push the last rowSelection item.
      } else {
        rowIndexes.push(lastRow);
      }

      // Sort the rowIndex array from smallest to largest.
      rowIndexes.sort((a, b) => a - b);

      // Empty row selection
      this.rowSelection = [];

      // Select all rows
      for (let i = rowIndexes[0]; i <= rowIndexes[rowIndexes.length - 1]; i += 1) {
        this.rowSelection.push(this.rowData[i]);
      }
    },

    // Selects rows on row click.
    selectRows(row, event) {
      // If metaKey is pressed & the row is not in the selection, add to selection
      if (!this.rowSelection.includes(row.id) && event.metaKey) {
        this.rowSelection.push(row.id);

        // If metaKey is pressed & the row is already in the selection, remove from selection.
      } else if (this.rowSelection.includes(row.id) && event.metaKey) {
        this.rowSelection.splice(this.rowSelection.indexOf(row.id), 1);

        // If the current row is the only row selected, go to the row detail when clicked again.
      } else if (this.rowSelection.length === 1 && this.rowSelection.includes(row.id) && !this.customEvents) {
        if (this.$store.state.meStore.me.rights.includes(`${this.permission}.edit`) || this.$store.state.meStore.me.su) {
          this.$router.push(`/${this.module}/${row.id}`);
        }

        // When using shift select, we need to select on row number due to sorted columns.
      } else if (event.shiftKey && this.rowSelection.length) {
        this.shiftSelect(row);

        // Simple click sets the clicked row as the rowSelection.
      } else {
        this.rowSelection = [row.id];
      }
    },
    // Api fetch method for vuetable
    async fetchData(apiUrl, httpOptions) {
      // Retrieve filters from the store
      const storeFilters = this.$store.state.filtersStore.filters[this.$route.name];
      if (storeFilters && storeFilters.general) {
        this.searchTerm = this.$store.state.filtersStore.filters[this.$route.name].general;
      } else if (storeFilters) {
        this.searchTerm = '';
        this.filters = this.$store.state.filtersStore.filters[this.$route.name];
      }
      // Vuetable sort is given in the form "sort=field|order"
      // Here, this is adjusted to our API as "sort[field]=order"
      let sortOptions = [];
      if (httpOptions.params.sort) {
        sortOptions = httpOptions.params.sort.split('|');
      }

      // Assign sortOptions to variables
      if (sortOptions.length === 2) {
        [this.sortField, this.sortOrder] = sortOptions;
      }

      // Only set params when we have updated values, otherwise give a null value.
      const perPage = (this.preferences.perPage !== this.perPage ? `perPage=${this.perPage}` : null);
      const page = (this.preferences.page !== this.page ? `page=${this.page}` : null);

      // This needs to be retained on page and per_page updates too.
      const preSort = (this.preferences.sortOrder !== this.sortOrder
        || this.preferences.sortField !== this.sortField
        || this.preferences.page !== this.page
        || this.preferences.per_page !== this.per_page
        ? `sort[${this.sortField}]=${this.sortOrder}&page=${this.page}` : null);

      // Assign preSort if sort is present in httpOptions
      let sort = httpOptions.params.sort === '' ? null : preSort;

      // Certain sort fields
      if (this.customSort) {
        switch (this.customSort.field) {
          case 'usergroup':
            sort = sort ? `${sort}&usergroup=${this.usergroupId}` : null;
            break;
          case 'user':
            sort = sort ? `${sort}&user=${this.data.id}` : null;
            break;

          default:
            break;
        }
      }

      // Create the params array for the request
      let params = [
        sort,
        perPage,
        page,
      ];

      // Update param preferences, here we keep track of them.
      this.preferences = {
        page: this.page,
        perPage: this.perPage,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
      };

      if (this.searchTerm !== '') {
        params.push(`general=${this.searchTerm}`);
      } else {
        // Get filters (originating from the TableFilter component)
        Object.keys(this.filters).forEach((key) => {
          // Allow only filters which do not contain empty strings
          if (this.filters[key] !== '' && this.filters[key] !== null) {
            // When the filter is an array of values, provide each param.
            if (Array.isArray(this.filters[key])) {
              this.filters[key].forEach((element) => {
                params.push(`${key}[]=${element}`);
              });
            } else {
              // Add non-empty filters to params array
              params.push(`${key}=${this.filters[key]}`);
            }
          }
        });
      }

      // Join params to form query string
      params = params.filter(val => val !== null).join('&');
      params = params && `/?${params}`;

      // Remove unneccesary forward slashes from the request
      const request = `${apiUrl}${params}`.replace('//', '/');

      const response = await this.axios.get(request);

      // Ensure missing pagination does not throw error.
      response.data.meta = response.data.meta || { pagination: {} };
      return response;
    },

    onError(error) {
      // When no rusults are availible, empty the table data.
      if (!error.response || error.response.status === 404) {
        this.isLoading = false;
        this.$refs.vuetable.tableData = [];
      }
    },

    noDataString(context) {
      if (this.isLoading) {
        return this.$store.state.stringsStore.language === 'nl' ? 'Bezig met laden...' : 'Loading...';
      }

      // ensure context is in lower case
      context = context.toLowerCase();
      const message = getString('list.nothingFound');

      // Exceptions:
      context = context === 'promotions' ? 'promotion' : context;

      if (context === 'plannings') {
        return getString(null, 'No plannings for the selected course', 'Geen plannings gevonden');
      }

      const replacement = getString(`module.name.${context}`).toLowerCase();

      return message.replace('%records%', replacement);
    },

    // Format time (callback defined in columnsDefinition)
    tableFormatTime(value, format) {
      if (!value) {
        return '-';
      }
      // Unix timestamp formatted as "2018-16-05, 11:36 pm.."
      // return value > 0 ? moment(value).format('YYYY-DD-MM, h:mm a') : '-';
      return moment(value).locale('nl').format(format || `${this.dateFormat[this.$store.state.meStore.me.settings.date]}
        ${this.$store.state.meStore.me.settings.time}`);
    },

    onRowClicked(row, event) {
      if (this.customEvents) {
        this.$emit('click', { row, event });
        if (this.selectable) {
          this.selectRows(row, event);
        }
        return;
      }
      // Checks if table is selectable.
      // True for tables using the selectRows mixin.
      if (this.selectable) {
        this.selectRows(row, event);

        // Allow navigation to context-edit only when the user has rights.
      } else if (!this.rowClickDisabled && (this.$store.state.meStore.me.rights.includes(`${this.permission}.edit`) || this.$store.state.meStore.me.su)) {
        this.$router.push(`/${this.module}/${row.id}`);
      }
    },

    // Adjust the gutter column width based on the visible scrollbar
    applyScrollBarAdjust() {
      setTimeout(() => {
        const table = document.getElementsByClassName('vuetable');
        const bodyWrapper = table && table[0] && table[0].scrollWidth;
        const tableWrapper = table && table[1] && table[1].scrollWidth;
        const diff = bodyWrapper - tableWrapper;
        const gutter = document.getElementsByClassName('vuetable-gutter-col')[0];
        if (!gutter) {
          return;
        }
        // Specific to old IE
        if (document.all) {
          gutter.style.setAttribute('cssText', `width: ${diff}px !important;`);
          // Modern browser
        } else {
          gutter.setAttribute('style', `width: ${diff}px !important;`);
        }
      }, 100);
    },

    onLoading() {
      this.isLoading = true;
    },

    onSuccess(response) {
      // Set loading to false
      this.isLoading = false;
      // Modify the response object to fit vuetable.
      response.data.data = response.data[this.responseFormat] || response.data[this.responseData.multiple];
      // For selectable tables, map row ids to an array.
      if (this.selectable) {
        this.rowData = response.data.data.map(row => row.id);
      }
    },

    dispatchTask(task) {
      if (task.runnable) {
        EventBus.$emit('task:start', task);
      }
    },
    messageClass(levelType) {
      return `${levelType}-level`;
    },
    notify(message = null, type = 'positive') {
      if (!message) {
        if (type === 'positive') {
          message = this.$store.state.stringsStore.language === 'nl' ? 'Bijgewerkt' : 'Updated';
        } else {
          message = this.$store.state.stringsStore.language === 'nl' ? 'Kan niet bijwerken' : 'Unable to update';
        }
      }
      this.$q.notify({
        message,
        position: 'top-right',
        timeout: 1000,
        color: type,
      });
    },

    getReferenceStatusClass(score) {
      if (score >= 90) {
        return 'show-status--green';
      }
      if (score >= 60) {
        return 'show-status--orange';
      }
    },
  },

  watch: {
    perPage() {
      // When perPage is changed, go to page 1.
      this.page = 1;

      // Only request new data when selected perPage is different from preferences.
      if (this.preferences.perPage !== this.perPage) {
        // Reload the table with adjusted number of rows.
        this.$nextTick(() => this.$refs.vuetable.refresh());
      }
    },

    rowSelection() {
      EventBus.$emit('row-selection:update', this.rowSelection);
    },
  },

  mounted() {
    // Register event listeners
    EventBus.$on('refresh-table', () => {
      this.$nextTick(() => {
        this.rowSelection = [];
        this.$refs.vuetable && this.$refs.vuetable.refresh();
      });
    });

    EventBus.$on('row-selection:delete', (key) => {
      this.removeRows(key);
    });
  },

  updated() {
    // Add column width for the scroll bar
    this.applyScrollBarAdjust();

    // Prevent default events for shift key & context menu
    this.selectable && document.addEventListener('selectstart', this.handleShiftKey, { passive: true });
    this.selectable && document.addEventListener('contextmenu', this.handleContextMenu, { passive: true });
  },

  destroyed() {
    // Clean up event listeners
    EventBus.$off('refresh-table');
    EventBus.$off('row-selection:delete');

    this.selectable && document.removeEventListener('selectstart', this.handleShiftKey);
    this.selectable && document.removeEventListener('contextmenu', this.handleContextMenu);
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables';
@import '../styles/components/tables.general.styl';
</style>
